import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/cn";
import { EmptyIllustration } from "@/shared/ui/EmptyIllustration";

type EmptyTone = "neutral" | "danger";

const BADGE: Record<EmptyTone, string> = {
  neutral: "bg-brand-soft text-brand-strong",
  danger: "bg-danger-soft text-danger",
};

const SIZES = {
  /* Pages and large panels. */
  md: { box: "py-16", art: "mb-5 w-[160px]", badge: "size-11 end-[18px] bottom-0", icon: "size-5" },
  /* Inside cards and side panels: compact, so a few of them stacked stay calm. */
  sm: { box: "py-8", art: "mb-4 w-[96px]", badge: "size-8 end-[10px] bottom-0", icon: "size-4" },
} as const;

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  body: string;
  action?: ReactNode;
  /** "danger" for errors: the badge turns red. */
  tone?: EmptyTone;
  /** "md" for pages, "sm" inside cards. */
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * Nothing to show yet, or something went wrong: a small illustration drawn with the theme's
 * tokens, the icon of what is missing on a badge, a title, a sentence and maybe an action.
 */
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  tone = "neutral",
  size = "md",
  className,
}: EmptyStateProps) {
  const sizes = SIZES[size];
  return (
    <div className={cn("flex flex-col items-center px-6 text-center", sizes.box, className)}>
      <div aria-hidden="true" className={cn("relative", sizes.art)}>
        <EmptyIllustration className="w-full" />
        <span
          className={cn(
            "absolute grid place-items-center rounded-full shadow-md ring-4 ring-surface",
            BADGE[tone],
            sizes.badge,
          )}
        >
          <Icon className={sizes.icon} strokeWidth={2} />
        </span>
      </div>
      <h2 className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
      <p className="mt-1.5 max-w-sm text-[14.5px] leading-relaxed text-ink-muted">{body}</p>
      {action ? <div className={size === "sm" ? "mt-4" : "mt-6"}>{action}</div> : null}
    </div>
  );
}
